import React from 'react';

/* ===========================================================================
   These mirror the components published in the "Vitality Foundation" Magic
   Patterns design system (Button, Section, SectionHeading, CircleFigure),
   class for class. They are duplicated locally so this artifact compiles
   standalone. If the design system's component import path is wired up later,
   delete this file and import from there instead. The markup is identical, so
   that swap is mechanical.

   All colors come from design system tokens. Never hardcode a hex here.
   =========================================================================== */

/* --------------------------------------------------------------- Button -- */

export type ButtonVariant = 'primary' | 'outline' | 'ghost';
export interface ButtonProps {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  withArrow?: boolean;
  href?: string;
  type?: 'button' | 'submit';
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  className?: string;
  children?: React.ReactNode;
  'aria-label'?: string;
}
const btnBase = 'inline-flex items-center justify-center gap-2 rounded-full border font-sans font-semibold uppercase tracking-label transition-transform duration-normal ease-standard hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-50';
const btnVariants: Record<ButtonVariant, string> = {
  primary: 'border-primary bg-primary text-primary-foreground hover:opacity-90',
  outline: 'border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground',
  ghost: 'border-transparent bg-transparent text-foreground hover:bg-secondary hover:text-secondary-foreground'
};
function Arrow() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" data-id="element-52" data-mp="3g9e2cs1tr7yw">
      <path d="M5 12h14" data-id="element-53" data-mp="3r14fa8dxrulv" />
      <path d="m12 5 7 7-7 7" data-id="element-54" data-mp="3r0cuztpshjaw" />
    </svg>;
}
export function Button({
  variant = 'primary',
  size = 'md',
  withArrow = true,
  href,
  type = 'button',
  disabled,
  onClick,
  className = '',
  children,
  'aria-label': ariaLabel
}: ButtonProps) {
  const cls = [btnBase, btnVariants[variant], size === 'sm' ? 'px-4 py-2 text-[10px]' : 'px-5 py-3 text-[11px]', className].filter(Boolean).join(' ');
  if (href) {
    /* External destinations (the intake form, donation links) open in a new tab
       so the visitor does not lose their place on the page. */
    const external = /^https?:\/\//.test(href);
    return <a href={href} className={cls} aria-label={ariaLabel} onClick={onClick} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined} data-id="element-55" data-mp="3c45ccxu14pxq">
        {children}
        {withArrow && <Arrow data-id="element-56" data-mp="rgimr2mnmt8p" />}
      </a>;
  }
  return <button type={type} disabled={disabled} className={cls} aria-label={ariaLabel} onClick={onClick} data-id="element-57" data-mp="zv6t7wilk0jy">
      {children}
      {withArrow && <Arrow data-id="element-58" data-mp="px8xknyer1hb" />}
    </button>;
}

/* -------------------------------------------------------------- Section -- */

export interface SectionProps {
  id?: string;
  tone?: 'default' | 'card' | 'deep';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  bordered?: boolean;
  bleed?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
  innerClassName?: string;
  children?: React.ReactNode;
}
const tones = {
  default: 'bg-background text-foreground',
  card: 'bg-card text-card-foreground',
  deep: 'theme-dark bg-background text-foreground'
};
const sectionSizes = {
  xs: 'py-10 lg:py-12',
  sm: 'py-16',
  md: 'py-20',
  lg: 'py-28'
};
export function Section({
  id,
  tone = 'default',
  size = 'md',
  bordered = false,
  bleed = false,
  className = '',
  innerClassName = '',
  children,
  ...aria
}: SectionProps) {
  const cls = [tones[tone], sectionSizes[size], bordered ? 'border-y border-border' : '', className].filter(Boolean).join(' ');
  return <section id={id} className={cls} {...aria} data-id="element-59" data-mp="8ohozbibyp77">
      {bleed ? children : <div className={`section-shell ${innerClassName}`.trim()} data-id="element-60" data-mp="3qg7gv0jyxxu">{children}</div>}
    </section>;
}

/* -------------------------------------------------------- SectionHeading -- */

export interface SectionHeadingProps {
  eyebrow?: string;
  title: React.ReactNode;
  lede?: React.ReactNode;
  as?: 'h1' | 'h2' | 'h3';
  scale?: 'display' | 'h1' | 'h2' | 'h3';
  align?: 'left' | 'center';
  id?: string;
  className?: string;
}
const scaleClass = {
  display: 'text-display',
  h1: 'text-h1',
  h2: 'text-h2',
  h3: 'text-h3'
};
export function SectionHeading({
  eyebrow,
  title,
  lede,
  as = 'h2',
  scale,
  align = 'left',
  id,
  className = ''
}: SectionHeadingProps) {
  const Tag = as as React.ElementType;
  const step = scale ?? (as === 'h1' ? 'h1' : as === 'h3' ? 'h3' : 'h2');
  const centered = align === 'center';
  return <div className={[centered ? 'text-center' : '', className].filter(Boolean).join(' ')} data-id="element-61" data-mp="1rps245y43r8j">
      {eyebrow ? <p className="overline" data-id="element-62" data-mp="1wp4ybhavians">{eyebrow}</p> : null}
      <Tag id={id} className={['font-serif font-normal', scaleClass[step], eyebrow ? 'mt-5' : ''].filter(Boolean).join(' ')} data-id="element-63" data-mp="21mwoa5ryjmpc">
        {title}
      </Tag>
      {lede ? <p className={['mt-6 max-w-[62ch] text-body leading-body text-muted-foreground', centered ? 'mx-auto' : ''].filter(Boolean).join(' ')} data-id="element-64" data-mp="2akz07gd8bzre">
          {lede}
        </p> : null}
    </div>;
}

/* --------------------------------------------------------- CircleFigure -- */

export interface CircleFigureProps {
  src: string;
  alt: string;
  label?: string;
  headline?: React.ReactNode;
  body?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}
const circleSizes = {
  sm: 'max-w-20',
  md: 'max-w-28',
  lg: 'max-w-44'
};
export function CircleFigure({
  src,
  alt,
  label,
  headline,
  body,
  size = 'lg',
  className = ''
}: CircleFigureProps) {
  return <figure className={['min-w-0 text-center', className].filter(Boolean).join(' ')} data-id="element-65" data-mp="y8tr3sc3livr">
      <img src={src} alt={alt} loading="lazy" width={512} height={512} className={`mx-auto aspect-square w-full rounded-full object-cover ${circleSizes[size]}`} data-id="element-66" data-mp="1cxzorbvaf11j" />
      {(label || headline || body) && <figcaption data-id="element-67" data-mp="1lx4mifz0yqcn">
          {label ? <p className="overline mt-6 text-foreground" data-id="element-68" data-mp="1hxcb7vi8y63l">{label}</p> : null}
          {headline ? <p className="mt-3 font-serif text-h4 leading-snug" data-id="element-69" data-mp="3nqan5pa2hy1x">{headline}</p> : null}
          {body ? <p className="mx-auto mt-3 max-w-56 text-body-sm leading-body text-muted-foreground" data-id="element-70" data-mp="3jqa9i6uslqvx">
              {body}
            </p> : null}
        </figcaption>}
    </figure>;
}